/**
 * Arrow head rendering utilities for connectors
 */

import { ConnectorArrowStyle } from '../editing/types';

export interface ArrowHeadConfig {
  x: number;
  y: number;
  angle: number;
  style: ConnectorArrowStyle;
  stroke: string;
  strokeWidth: number;
}

/**
 * Get arrow head rendering configuration
 */
export function getArrowHeadConfig({
  x,
  y,
  angle,
  style,
  stroke,
  strokeWidth,
}: ArrowHeadConfig): {
  fill: string | undefined;
  stroke: string | undefined;
  strokeWidth: number;
  sceneFunc: (context: any, shape: any) => void;
} | null {
  if (style === 'none') {
    return null;
  }
  
  // Calculate arrow dimensions proportional to stroke width
  // Base size scales with stroke width for better appearance
  const baseSize = Math.max(10, strokeWidth * 3);
  const arrowLength = baseSize * 1.3; // Length of arrow head
  const arrowWidth = baseSize * 1.8;  // Width of arrow base for sharp, modern look
  
  const sceneFunc = (context: any, shape: any) => {
    context.save();
    context.translate(x, y);
    context.rotate(angle);
    
    // Set line properties for sharp, modern appearance
    context.lineCap = 'square';
    context.lineJoin = 'miter';
    context.miterLimit = 10;
    
    context.beginPath();
    
    switch (style) {
      case 'solid':
        // Solid filled triangle (outward pointing) - modern sharp style
        // Arrow extends backward from the line endpoint
        // Tip is at (0,0) which is the line endpoint, base extends backward
        context.moveTo(0, 0);
        context.lineTo(-arrowLength, -arrowWidth / 2);
        context.lineTo(-arrowLength, arrowWidth / 2);
        context.closePath();
        context.fillStyle = stroke;
        context.fill();
        // Add clean border matching connector style
        context.strokeStyle = stroke;
        context.lineWidth = Math.max(0.5, strokeWidth * 0.2);
        context.stroke();
        break;
        
      case 'line':
        // Outlined arrow (outward pointing)
        context.moveTo(0, 0);
        context.lineTo(-arrowLength, -arrowWidth / 2);
        context.lineTo(-arrowLength, arrowWidth / 2);
        context.closePath();
        context.strokeStyle = stroke;
        context.lineWidth = strokeWidth;
        context.stroke();
        break;
        
      case 'triangle':
        // Triangle pointing inward
        context.moveTo(0, 0);
        context.lineTo(arrowLength, -arrowWidth / 2);
        context.lineTo(arrowLength, arrowWidth / 2);
        context.closePath();
        context.fillStyle = stroke;
        context.fill();
        context.strokeStyle = stroke;
        context.lineWidth = Math.max(1, strokeWidth * 0.3);
        context.stroke();
        break;
        
      case 'diamond':
        // Diamond shape
        context.moveTo(0, 0);
        context.lineTo(-arrowLength / 2, -arrowWidth / 2);
        context.lineTo(-arrowLength, 0);
        context.lineTo(-arrowLength / 2, arrowWidth / 2);
        context.closePath();
        context.fillStyle = stroke;
        context.fill();
        context.strokeStyle = stroke;
        context.lineWidth = Math.max(1, strokeWidth * 0.3);
        context.stroke();
        break;
        
      case 'circle':
        // Circle endpoint
        const circleRadius = baseSize * 0.8;
        context.arc(-circleRadius, 0, circleRadius, 0, Math.PI * 2);
        context.fillStyle = stroke;
        context.fill();
        context.strokeStyle = stroke;
        context.lineWidth = Math.max(1, strokeWidth * 0.3);
        context.stroke();
        break;
    }
    
    context.restore();
    
    // Apply Konva shape properties (fillStrokeShape applies fill/stroke from shape props)
    // Since we've already drawn fill/stroke in sceneFunc, this ensures proper rendering
    // @ts-ignore - Konva context has fillStrokeShape method
    context.fillStrokeShape(shape);
  };
  
  // Return config - sceneFunc draws everything, fillStrokeShape applies shape properties
  return {
    fill: style === 'line' ? undefined : stroke,
    stroke: undefined, // All stroke handled in sceneFunc for precise control
    strokeWidth: 0, // All stroke width handled in sceneFunc
    sceneFunc,
  };
}

/**
 * Calculate arrow angle from path points
 * Returns angle in radians
 */
export function calculateArrowAngle(
  path: number[],
  isStart: boolean
): number {
  if (path.length < 4) {
    return 0;
  }
  
  if (isStart) {
    // Angle at start point
    if (path.length === 4) {
      // Straight line
      const x1 = path[0];
      const y1 = path[1];
      const x2 = path[2];
      const y2 = path[3];
      return Math.atan2(y2 - y1, x2 - x1) + Math.PI; // Reverse direction
    } else if (path.length === 8) {
      // Bezier curve - use first control point
      const x1 = path[0];
      const y1 = path[1];
      const cp1x = path[2];
      const cp1y = path[3];
      return Math.atan2(cp1y - y1, cp1x - x1) + Math.PI;
    } else {
      // Multi-segment - use first segment
      const x1 = path[0];
      const y1 = path[1];
      const x2 = path[2];
      const y2 = path[3];
      return Math.atan2(y2 - y1, x2 - x1) + Math.PI;
    }
  } else {
    // Angle at end point
    if (path.length === 4) {
      // Straight line
      const x1 = path[0];
      const y1 = path[1];
      const x2 = path[2];
      const y2 = path[3];
      return Math.atan2(y2 - y1, x2 - x1);
    } else if (path.length === 8) {
      // Bezier curve - use last control point
      const x2 = path[6];
      const y2 = path[7];
      const cp2x = path[4];
      const cp2y = path[5];
      return Math.atan2(y2 - cp2y, x2 - cp2x);
    } else {
      // Multi-segment - use last segment
      const len = path.length;
      const x1 = path[len - 4];
      const y1 = path[len - 3];
      const x2 = path[len - 2];
      const y2 = path[len - 1];
      return Math.atan2(y2 - y1, x2 - x1);
    }
  }
}

